import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../providers/firebase/firebase';
import type { SubscriptionDoc } from '../../types/notifications';
import type { AlertSeverity } from '../../pages/markets/api/types';

interface SubscribeFields {
    alert_types: string[] | null;
    min_severity: AlertSeverity;
    /** Human-readable event name, persisted for the subscriptions table. */
    event_title?: string;
}

interface UseSubscriptionResult {
    subscription: SubscriptionDoc | null;
    loading: boolean;
    subscribe: (fields: SubscribeFields) => Promise<void>;
    update: (fields: Partial<SubscribeFields>) => Promise<void>;
    unsubscribe: () => Promise<void>;
}

export function useSubscription(eventId: string): UseSubscriptionResult {
    const [uid, setUid] = useState<string | null>(null);
    const [subscription, setSubscription] = useState<SubscriptionDoc | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const auth = getAuth();
        if (auth.currentUser) setUid(auth.currentUser.uid);
        return onAuthStateChanged(auth, u => setUid(u?.uid ?? null));
    }, []);

    useEffect(() => {
        if (!uid || !eventId) return;
        const ref = doc(db, `users/${uid}/subscriptions/${eventId}`);
        const unsub = onSnapshot(
            ref,
            snap => {
                setSubscription(
                    snap.exists() ? (snap.data() as SubscriptionDoc) : null
                );
                setLoading(false);
            },
            // Without this the button's placeholder would stay up for good.
            () => setLoading(false)
        );
        return () => unsub();
    }, [uid, eventId]);

    const subscribe = useCallback(async (fields: SubscribeFields) => {
        if (!uid) return;
        await setDoc(doc(db, `users/${uid}/subscriptions/${eventId}`), {
            event_id: eventId,
            event_title: fields.event_title ?? eventId,
            created_at: serverTimestamp(),
            alert_types: fields.alert_types,
            min_severity: fields.min_severity,
        });
    }, [uid, eventId]);

    const update = useCallback(async (fields: Partial<SubscribeFields>) => {
        if (!uid) return;
        await updateDoc(doc(db, `users/${uid}/subscriptions/${eventId}`), { ...fields });
    }, [uid, eventId]);

    const unsubscribe = useCallback(async () => {
        if (!uid) return;
        await deleteDoc(doc(db, `users/${uid}/subscriptions/${eventId}`));
    }, [uid, eventId]);

    return { subscription, loading, subscribe, update, unsubscribe };
}
